import React, { useEffect, useMemo, useState } from 'react';
import {
	CardContent,
	Divider,
	TextField,
	FormHelperText,
	CircularProgress,
	FormLabel,
	Container,
	Grid,
	useTheme,
} from '@mui/material';
import { Formik, Form, useFormikContext } from 'formik';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useDispatch, useSelector } from 'react-redux';
import { InstantClientSelect } from '@/components/selects/ClientSelect';
import FileUpload from '@/components/FileUpload';
import { PageHeader } from '@/components/layouts/PageHeader';
import { showToast } from '@/features/toast/toastSlice';
import { useLocation } from 'react-router-dom';
import { FormikErrorScroll, useFormikHelper } from '@/hooks/useFormikHelper';
import { reset } from '@/features/select/clientSelectSlice';
import { OwnerSelect } from '@/components/selects/OwnerSelect';
import {
	BROKER_GROUP_ID,
	CLIENT_ID,
	CLIENT_NAME,
	DEAL_NAME,
	OWNER_DOC_ID,
	YEAR_FOLDER,
} from '../CreateNewBusinessForm/validationSchema';
import { paths } from '@/app/routes';
import { useGetClientPropertiesQuery } from '@/features/clients/clientsApi';
import { NOTES_HELP_TEXT, SEARCH_CLIENT_HELP_TEXT } from '@/utils/helperText';
import {
	default as folderValidationSchema,
	FOLDER_NAME,
} from '@/components/dialogs/AddFolderSharepointDialog/validationSchema';
import { validationSchema } from './validationSchema';
import DealNaming from '../fields/DealNaming';
import FormNavigationBlocker from '../FormNavigationBlocker';
import { useCreateDebtorMutation } from '@/features/deals/dealsApi';
import { DebtorsIcon } from '@/components/icons/DebtorsIcon';
import DealFormMessages from '@/components/forms/CreateFormFields/DealFormMessages';
import DealFormActions from '@/components/forms/CreateFormFields/DealFormActions';
import DebtorPolicyTransactionSelect from '@/components/forms/CreateDebtorForm/DebtorPolicyTransactionSelect';

export const CreateDebtorForm = () => {
	const dispatch = useDispatch();
	const location = useLocation();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const existingClientId = useMemo(() => location?.state?.clientId, [location?.state?.clientId]);
	const [createDebtor] = useCreateDebtorMutation();

	const { data: clientData } = useGetClientPropertiesQuery(existingClientId, {
		skip: !existingClientId,
	});
	const clientRow = clientData?.clientRow;

	const existingClient = useMemo(() => {
		return clientRow ?? location?.state?.client ?? {};
	}, [location?.state?.client, clientRow]);

	const [formLoading, setFormLoading] = useState(false);

	const initialValues = {
		insightId: clientRow?.insightId ?? clientData?.insight?.Id ?? '',
		transactions: location?.state?.transactions ?? [],
		files: [],
		clientName: clientRow?.name ?? existingClient?.name ?? '',
		[OWNER_DOC_ID]: userDetails?.userId,
		successMessage: '',
		notes: '',
		errorMessage: '',
		[YEAR_FOLDER]: null,
		[DEAL_NAME]: '',
		[FOLDER_NAME]: '',
		[CLIENT_ID]: clientRow?.clientId ?? existingClientId ?? '',
		[BROKER_GROUP_ID]: clientRow?.brokerGroupId ?? userDetails?.brokerGroupId,
	};

	const [schema, setSchema] = useState(validationSchema);
	const [brokerGroupId, setBrokerGroupId] = useState(userDetails?.brokerGroupId);

	useEffect(() => {
		if (!brokerGroupId && userDetails?.brokerGroupId)
			setBrokerGroupId(userDetails.brokerGroupId);
	}, [brokerGroupId, userDetails?.brokerGroupId]);

	const { setUpDealFilesAndFolders } = useClientStorageHooks(brokerGroupId);

	const createDealFolders = async ({ clientId, dealId, noteId, values }) => {
		try {
			await setUpDealFilesAndFolders({
				dealId,
				noteId,
				clientId,
				clientName: values.clientName,
				dealName: values[FOLDER_NAME],
				pipelineId: process.env.REACT_APP_PIPELINE_DEBTORS,
				files: values.files,
				yearFolder: values.yearFolder,
				notes: values.notes,
			});
		} catch (error) {
			console.log('🚀 ~ Error creating debtors folders', error);
			return {
				error: {
					message:
						'Debtor created, but there was an error creating debtor folder or uploading files',
				},
			};
		}
	};

	const createNewDebtor = async (values) => {
		const body = {
			ownerDocId: values[OWNER_DOC_ID] ?? userDetails?.userId,
			dealName: values[FOLDER_NAME].trim(),
			notes: values.notes,
			clientId: values.clientId,
			transactions: values.transactions,
			[YEAR_FOLDER]: values[YEAR_FOLDER],
			[BROKER_GROUP_ID]: values[BROKER_GROUP_ID],
		};
		return await createDebtor(body)
			.unwrap()
			.catch((err) => {
				console.log('🚀 ~ Error creating debtor', err);
				return { error: { message: 'Error creating debtor' } };
			});
	};

	return (
		<Formik
			initialValues={initialValues}
			validateOnChange={false}
			validationSchema={schema}
			onSubmit={async (values, { resetForm, setFieldValue, setSubmitting }) => {
				setFieldValue('errorMessage', '');
				setFormLoading(true);
				console.log('📋 ~ Formik values', values);
				const createError = (message) => {
					setFieldValue('errorMessage', message ?? 'Error creating debtor');
					setFieldValue('successMessage', '');
					setSubmitting(false);
					setFormLoading(false);
				};
				const res = await createNewDebtor(values);
				if (res?.error) {
					console.log('🙅 ~ Error creating Debtor', res?.error);
					createError(res?.error?.message);
					return;
				}
				const response = await createDealFolders({
					clientId: values.clientId,
					dealId: res.debtorId,
					notesId: res.noteId,
					values,
				});
				if (response?.error) {
					createError(response?.error?.message);
					dispatch(
						showToast({
							message: response?.error?.message ?? 'Debtor created',
							autohide: false,
						}),
					);
					return;
				}
				console.log('🚀 ~ Successfully created new Debtor', res);

				dispatch(
					showToast({
						message: 'New debtor created!',
						action: {
							path: `${paths.debtors}/${res.debtorId}`,
							label: 'GO TO DEBTOR',
						},
						autohide: false,
					}),
				);
				window.scrollTo(0, 0);
				window.history.replaceState({}, '');
				resetForm();
				dispatch(reset());
				setFieldValue('successMessage', 'Successfully created Debtor');
				setFieldValue('errorMessage', '');
				setSubmitting(false);
				setFormLoading(false);
			}}
		>
			{() => {
				return (
					<CreateDebtorFormBody
						loading={formLoading}
						setBrokerGroupId={setBrokerGroupId}
						setSchema={setSchema}
					/>
				);
			}}
		</Formik>
	);
};

const CreateDebtorFormBody = ({ loading, setBrokerGroupId, setSchema }) => {
	const theme = useTheme();
	const { setFieldValue, values, errors, isSubmitting, touched, getFieldProps } =
		useFormikContext();
	const { getError, getErrorMessage } = useFormikHelper();

	const location = useLocation();
	const existingClientId = location?.state?.clientId;

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const {
		data: userDetails,
		isLoading,
		isUninitialized,
	} = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { data: clientData } = useGetClientPropertiesQuery(values[CLIENT_ID], {
		skip: !values[CLIENT_ID],
	});
	const clientRow = clientData?.clientRow;

	const updateClient = (client) => {
		if (!client) return;
		const brokerGroupId = client.brokerGroupId ?? userDetails?.brokerGroupId;
		setFieldValue(CLIENT_NAME, client.name ?? '');
		setFieldValue('insightId', client.insightId ?? client.insight?.Id ?? '');
		setFieldValue(CLIENT_ID, client.clientId);
		setFieldValue(BROKER_GROUP_ID, brokerGroupId);
		setBrokerGroupId(brokerGroupId);
	};

	useEffect(() => {
		setSchema(
			validationSchema
				.omit([FOLDER_NAME])
				.concat(folderValidationSchema(values.existingFolders, 'Debtor name')),
		);
	}, [values.existingFolders]);

	useEffect(() => {
		if (clientRow) {
			updateClient(clientRow);
		}
	}, [clientRow, userDetails?.brokerGroupId]);

	const updateClientSelect = (newClient) => {
		if (newClient) {
			updateClient(newClient);
		} else {
			setFieldValue(CLIENT_ID, '');
			setFieldValue(CLIENT_NAME, '');
			setFieldValue('insightId', '');
			setFieldValue('dealIds', []);
			setFieldValue('dealName', '');
			setFieldValue(BROKER_GROUP_ID, userDetails?.brokerGroupId);
			setBrokerGroupId(userDetails?.brokerGroupId);
		}
	};

	const gridProps = {
		rowSpacing: 1,
		columnSpacing: 2,
	};

	return (
		<Container sx={{ pt: 3 }}>
			<Form>
				<FormNavigationBlocker />
				<FormikErrorScroll isSubmitting={isSubmitting} />
				<PageHeader
					title={'Add Debtor'}
					icon={
						<DebtorsIcon
							width='40px'
							height='40px'
							{...(theme.palette.mode === 'dark' && { bgColor: 'transparent' })}
						/>
					}
				/>

				<CardContent>
					{isLoading || isUninitialized ? (
						<CircularProgress />
					) : (
						<Grid container {...gridProps}>
							<DealNaming dealName='Debtor name' />
							<Grid size={12}>
								<FormLabel required error={getError(CLIENT_ID)}>
									{existingClientId ? 'Client name' : 'Search client name'}
								</FormLabel>
								<InstantClientSelect
									clientId={values.clientId}
									clientName={values.clientName}
									filterProspects={true}
									setClient={updateClientSelect}
									disabled={isSubmitting || existingClientId}
									error={getError(CLIENT_ID)}
								/>
								<FormHelperText error={getError(CLIENT_ID)}>
									{getErrorMessage(CLIENT_ID) || SEARCH_CLIENT_HELP_TEXT}
								</FormHelperText>
							</Grid>
							<Grid size={12}>
								<DebtorPolicyTransactionSelect />
							</Grid>
							<Grid size={12}>
								<FormLabel>{'Assignee'}</FormLabel>
								<OwnerSelect
									initialId={values[OWNER_DOC_ID]}
									disabled={isSubmitting}
									showGroup={true}
									onChange={(user) => {
										setFieldValue(OWNER_DOC_ID, user?.id);
									}}
								/>
							</Grid>
							<Grid size={12}>
								<FormLabel>Notes</FormLabel>
								<TextField
									fullWidth
									id='notes'
									multiline
									rows={4}
									{...getFieldProps('notes')}
									disabled={isSubmitting}
									error={touched.notes && Boolean(errors.notes)}
									helperText={
										touched.notes && errors.notes
											? errors.notes
											: NOTES_HELP_TEXT
									}
								/>
							</Grid>

							<Grid size={12}>
								<FileUpload
									files={values.files}
									setFiles={(files) => setFieldValue('files', files)}
									loading={isSubmitting}
								/>
							</Grid>
							<DealFormMessages />
						</Grid>
					)}
				</CardContent>
				{/* <DevJSONView value={values} /> */}
				<Divider />
				<DealFormActions
					disabled={isLoading || isUninitialized || loading || isSubmitting}
				/>
			</Form>
		</Container>
	);
};
